import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { revealUp, EASE_OUT } from '../lib/anim';
import { PROJECTS_DATA } from '../data/portfolio';
import { PROJECTS } from '../constants/testIds/portfolio';




function ProjectCard({ project, i }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: '50%', y: '50%' });
  const [hover, setHover] = useState(false);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    setPos({
      x: `${((e.clientX - rect.left) / rect.width) * 100}%`,
      y: `${((e.clientY - rect.top) / rect.height) * 100}%`,
    });
  };

  return (
    <motion.article
      data-testid={PROJECTS.card(project.id)}
      variants={revealUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
      custom={i}
      className="group"
    >
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="relative aspect-[4/5] w-full overflow-hidden rounded-[3px] border border-border bg-black"
      >
        {/* Base grayscale poster */}
        <motion.img
          style={{ y: imgY }}
          src={project.image}
          alt={project.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full scale-[1.06] object-cover grayscale contrast-[1.05] brightness-[0.92] transition-all duration-[900ms] ease-out group-hover:scale-[1.02] group-hover:grayscale-0 group-hover:brightness-100"
        />

        {/* Coloured overlay reveal — fades in on hover for a punchy pop */}
        <img
          src={project.image}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full scale-[1.02] object-cover opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100"
        />

        {/* Subtle vignette so type over it always reads */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/30 opacity-90 transition-opacity duration-500 group-hover:opacity-40" />

        {/* Spotlight following cursor */}
        <div
          className="pointer-events-none absolute inset-0 transition-opacity duration-300"
          style={{
            opacity: hover ? 1 : 0,
            background: `radial-gradient(320px circle at ${pos.x} ${pos.y}, rgba(255,255,255,0.14), transparent 60%)`,
            mixBlendMode: 'overlay',
          }}
        />

        {/* Top-left meta */}
        <div className="absolute left-5 top-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-white/95">
          <span>{project.index}</span>
          <span className="h-px w-6 bg-white/50" />
          <span>{project.year}</span>
        </div>

        {/* Top-right hover CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.75 }}
          animate={{ opacity: hover ? 1 : 0, scale: hover ? 1 : 0.75 }}
          transition={{ duration: 0.35, ease: EASE_OUT }}
          className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground"
          data-testid={PROJECTS.cta(project.id)}
        >
          <ArrowUpRight className="h-5 w-5" />
        </motion.div>

        {/* Bottom accent line — draws in from left on hover */}
        <div
          className="absolute bottom-0 left-0 h-[3px] w-0 transition-all duration-700 group-hover:w-full"
          style={{ backgroundColor: project.accent }}
        />
      </div>

      <div className="mt-5 flex flex-col justify-between gap-3 md:flex-row md:items-start">
        <div>
          <h3 className="font-display text-2xl font-semibold tracking-tight text-foreground md:text-[26px]">
            {project.title}
          </h3>
          <p className="mt-1 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
            {project.category}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 md:max-w-[220px] md:justify-end">
          {project.tags.map((t) => (
            <span
              key={t}
              className="rounded-full border border-border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
      <p className="mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground">
        {project.description}
      </p>
    </motion.article>
  );
}

export default function Projects() {
  return (
    <section
      id="work"
      data-testid={PROJECTS.section}
      className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32"
    >
      <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            (Selected Work)
          </span>
          <h2 className="mt-4 font-display text-5xl font-extrabold leading-[0.9] tracking-tighter text-foreground md:text-7xl">
            Case
            <br />
            Studies
          </h2>
        </div>
        <p className="max-w-sm text-base leading-relaxed text-muted-foreground">
          A selection of posters, covers and identity systems I&apos;ve shaped
          end-to-end — hover over any piece to reveal it in full colour.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
        {PROJECTS_DATA.map((p, i) => (
          <ProjectCard key={p.id} project={p} i={i} />
        ))}
      </div>
    </section>
  );
}
